import { getCallLog, getEsTrendingTopics, getTrendingTopics, isElasticSearchEnabled } from '@/services/call-log-service/call-log-service';
import {
  Call,
  CallLogFilters,
  CallsResponse,
  TrendingTopicsParams,
} from '@/services/call-log-service/call-log-service.types';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { OrderBy } from '../use-sort-and-pagination/use-sort-and-pagination.types';
import { useWebsocket } from '../use-websocket/use-websocket';

interface UseCallLogProps {
  filtersParams: CallLogFilters;
  paginationParams: {
    sort_by?: string;
    order_by: OrderBy;
    page: number;
    items_per_page: number;
  };
}

export const useCallLog = ({ filtersParams, paginationParams }: UseCallLogProps) => {
  const queryClient = useQueryClient();
  const searchBackend = isElasticSearchEnabled() ? 'es' : 'pg';
  const {
    data: callLogData,
    error,
    isPending,
    refetch,
  } = useQuery({
    queryKey: [...ReactQueryKeys.CALL_LOG({ ...filtersParams, ...paginationParams }), searchBackend],
    queryFn: () => getCallLog({ ...filtersParams, ...paginationParams }),
    staleTime: Infinity,
  });

  const trendingTopicsParams: TrendingTopicsParams = {
    time_range: filtersParams.time_range,
    duration_min: filtersParams.duration_min,
    duration_max: filtersParams.duration_max,
    query: filtersParams.query,
    status: filtersParams.status,
    agent: filtersParams.agent,
    filters: filtersParams.filters,
    company_name: filtersParams.company_name,
    type: filtersParams.type,
    topic: filtersParams.topic,
  };

  const { data: trendingTopics, isPending: isPendingTrendingTopics } = useQuery({
    queryKey: [...ReactQueryKeys.TRENDING_TOPICS(trendingTopicsParams), searchBackend],
    queryFn: () => getTrendingTopics(trendingTopicsParams),
    staleTime: Infinity,
    enabled: !isElasticSearchEnabled(),
  });

  const { data: esTrendingTopicsData, isPending: isPendingEsTrendingTopics } = useQuery({
    queryKey: [...ReactQueryKeys.TRENDING_TOPICS(trendingTopicsParams), 'es-detailed'],
    queryFn: () => getEsTrendingTopics(trendingTopicsParams),
    staleTime: Infinity,
    enabled: isElasticSearchEnabled(),
  });

  const resolvedTrendingTopics = isElasticSearchEnabled()
    ? esTrendingTopicsData?.topics.map((t) => t.name)
    : trendingTopics;

  const resolvedIsPendingTrendingTopics = isElasticSearchEnabled()
    ? isPendingEsTrendingTopics
    : isPendingTrendingTopics;

  useWebsocket<{ call: Call }>({ channel: 'CallsChannel' }, ({ call: updatedCall }) => {
    queryClient.setQueryData(
      [...ReactQueryKeys.CALL_LOG({ ...filtersParams, ...paginationParams }), searchBackend],
      (oldCalls: CallsResponse | undefined) => {
        if (!oldCalls) {
          console.warn('No old calls data found in query cache.');
          return undefined;
        }

        const updatedCalls = oldCalls.calls.map((call) =>
          call.id === updatedCall.id ? { ...call, topics: [...new Set(updatedCall.topics)] } : call
        );

        return { ...oldCalls, calls: updatedCalls };
      }
    );
  });

  return {
    callLogData,
    error,
    isPending,
    refetch,
    trendingTopics: resolvedTrendingTopics,
    isPendingTrendingTopics: resolvedIsPendingTrendingTopics,
    esTrendingTopicsData,
  };
};
